import React from "react";
import { Link } from "react-router-dom";
import { FolderOpen } from "lucide-react";

export interface EmptyStateAction {
  label: string;
  onClick?: () => void;
  to?: string;
  icon?: React.ReactNode;
}

export interface EmptyStateProps {
  title: string;
  description: string;
  icon?: React.ReactNode;
  action?: EmptyStateAction;
  secondaryAction?: EmptyStateAction;
  variant?: "default" | "card" | "chart" | "compact" | "table";
  className?: string;
  "data-testid"?: string;
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  title,
  description,
  icon,
  action,
  secondaryAction,
  variant = "default",
  className = "",
  "data-testid": testId = "empty-state",
}) => {
  const isCompact = variant === "compact";
  const isChart = variant === "chart";
  const isTable = variant === "table";

  const content = (
    <div
      data-testid={testId}
      className={`flex flex-col items-center justify-center text-center ${
        isChart
          ? "h-full min-h-[200px] py-6 px-4"
          : isCompact
          ? "py-4 px-3"
          : isTable
          ? "py-8 px-4"
          : "py-10 px-6"
      } ${className}`}
    >
      {/* Icon with soft circular background */}
      <div
        className={`flex items-center justify-center rounded-full bg-[#E6F7F5] text-[#15BBAE] ${
          isCompact ? "h-10 w-10 mb-2" : "h-14 w-14 mb-3.5"
        }`}
        aria-hidden="true"
      >
        {icon || <FolderOpen size={isCompact ? 20 : 26} className="stroke-[2]" />}
      </div>

      {/* Title */}
      <h4
        className={`font-semibold text-[#10253A] ${
          isCompact ? "text-sm" : isChart ? "text-sm" : "text-base"
        }`}
      >
        {title}
      </h4>

      {/* Description */}
      <p
        className={`mt-1 max-w-sm text-[#63788E] ${
          isCompact ? "text-xs" : "text-xs sm:text-sm"
        }`}
      >
        {description}
      </p>

      {/* Actions */}
      {(action || secondaryAction) && (
        <div className="mt-4 flex flex-wrap items-center justify-center gap-2.5">
          {action && (
            action.to ? (
              <Link
                to={action.to}
                className="inline-flex items-center gap-1.5 rounded-lg bg-[#15BBAE] px-3.5 py-2 text-xs font-semibold text-white shadow-sm hover:bg-[#13a89d] transition-colors focus:outline-none focus:ring-2 focus:ring-[#15BBAE] focus:ring-offset-1"
              >
                {action.icon}
                <span>{action.label}</span>
              </Link>
            ) : (
              <button
                type="button"
                onClick={action.onClick}
                className="inline-flex items-center gap-1.5 rounded-lg bg-[#15BBAE] px-3.5 py-2 text-xs font-semibold text-white shadow-sm hover:bg-[#13a89d] transition-colors focus:outline-none focus:ring-2 focus:ring-[#15BBAE] focus:ring-offset-1 cursor-pointer"
              >
                {action.icon}
                <span>{action.label}</span>
              </button>
            )
          )}

          {secondaryAction && (
            secondaryAction.to ? (
              <Link
                to={secondaryAction.to}
                className="inline-flex items-center gap-1.5 rounded-lg border border-[#E4EAF0] bg-white px-3.5 py-2 text-xs font-medium text-[#63788E] hover:bg-neutral-50 hover:text-[#10253A] transition-colors"
              >
                {secondaryAction.icon}
                <span>{secondaryAction.label}</span>
              </Link>
            ) : (
              <button
                type="button"
                onClick={secondaryAction.onClick}
                className="inline-flex items-center gap-1.5 rounded-lg border border-[#E4EAF0] bg-white px-3.5 py-2 text-xs font-medium text-[#63788E] hover:bg-neutral-50 hover:text-[#10253A] transition-colors cursor-pointer"
              >
                {secondaryAction.icon}
                <span>{secondaryAction.label}</span>
              </button>
            )
          )}
        </div>
      )}
    </div>
  );

  return content;
};

export default EmptyState;
